import React, { FC, useState, useEffect } from 'react';
import { DropdownComponent } from 'ReusableComponents/Dropdown/DropdownComponent';
import { ButtonComponent } from 'ReusableComponents/Button/ButtonComponent';
import { InputComponent } from 'ReusableComponents/Input/InputComponent';
import './BillingCsvReport.scss';
import { downloadFile } from 'utils/fetchDownload';
import { queryStringFromObject as queryString } from 'utils/urlUtil';
import { CircleProgressComponent } from 'ReusableComponents/Progress/ProgressComponent';
import { ComboBoxComponent } from 'ReusableComponents/ComboBox/ComboBoxComponent';
import { fetchAccounts } from 'fetch/fetchUsers';
import { AccountCategory } from 'types/userTypes';
import { ComboBoxItem } from 'ReusableComponents/ComboBox/ComboBoxComponent.types';

import dayjs from 'dayjs';
import localeDataPlugin from 'dayjs/plugin/localeData';

// allow for dayjs().localeData()
dayjs.extend(localeDataPlugin);

const dropdownMonths = (year: string) =>
  dayjs()
    .localeData()
    .months()
    .filter((month: string, i: number) =>
      year === dayjs().get('year').toString() ? dayjs().get('month') > i : month
    )
    .map((month: string, i: number) => ({ name: month, value: i }));

const FetchCsvButton: FC<{
  date: Date;
  retailer?: ComboBoxItem;
  disabled: boolean;
}> = ({ date, retailer, disabled }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchMonthCsv = async (
    date: Date,
    retailer?: ComboBoxItem
  ): Promise<void> => {
    const startDate = dayjs(date);
    const endDate = startDate.add(1, 'month');

    const start = startDate.format('YYYY-MM-DD');
    const end = endDate.format('YYYY-MM-DD');

    const retailerName = retailer?.name ?? 'ALL';
    const retailerQuery = retailer ? `&retailerId=${retailer.value}` : '';
    const queryParams = queryString({ start, end }) + retailerQuery;
    const filename = `billing_report_${retailerName}_${start}_${end}.csv`;

    setIsLoading(true);
    await downloadFile(`reports/billing-csv${queryParams}`, filename);
    setIsLoading(false);
  };

  return isLoading ? (
    <CircleProgressComponent variant="indeterminate" />
  ) : (
    <ButtonComponent
      className="csv-report__download-container__button"
      onClick={() => fetchMonthCsv(date, retailer)}
      disabled={disabled}
    >
      Download
    </ButtonComponent>
  );
};

const BackgroundCsvReport: FC = () => {
  const lastMonth = dayjs().subtract(1, 'month');

  const [allRetailers, setAllRetailers] = useState<ComboBoxItem[]>([]);
  const [retailer, setRetailer] = useState<ComboBoxItem>();
  const [month, setMonth] = useState<number>(lastMonth.month());
  const [year, setYear] = useState<string>(lastMonth.year().toString());
  const [months, setMonths] = useState(dropdownMonths(year));
  const isValidYear =
    Number.isInteger(+year) && Number(year) <= lastMonth.year();
  const canFetch = Number.isInteger(month) && year && isValidYear;

  useEffect(() => {
    (async () => {
      const senders = await fetchAccounts(AccountCategory.SENDER);
      setAllRetailers(
        senders.map(account => ({
          name: account.name,
          value: account.id.toString(),
        }))
      );
    })();
  }, []);

  useEffect(() => {
    setMonths(dropdownMonths(year));
  }, [year]);

  return (
    <>
      <h1 className="csv-report-hero">Background report by month</h1>
      <div className="csv-report">
        <ComboBoxComponent
          className="csv-report__retailer"
          id="csvRetailer"
          variant="standard"
          value={retailer}
          comboBoxItems={allRetailers}
          comboBoxName="Retailer"
          onChange={((_: any, item: ComboBoxItem) => setRetailer(item)) as any}
        />
        <InputComponent
          className="csv-report__year"
          id="csvYear"
          label="Year"
          placeholder="YYYY"
          name="csvYear"
          error={!isValidYear}
          helperText={isValidYear ? null : 'Invalid year value'}
          value={year ?? ''}
          onChange={e => setYear(e.currentTarget.value)}
          fullWidth
        />
        <DropdownComponent
          className="csv-report__dropdown"
          id="csvMonth"
          label="Month"
          labelId="csv-month"
          value={month < months.length ? month : lastMonth.month()}
          onChange={(e: any) => setMonth(+e.target.value)}
          itemsToSelect={months}
        />
        <div className="csv-report__download-container">
          <FetchCsvButton
            date={new Date(+year, month, 1)}
            retailer={retailer}
            disabled={!canFetch}
          />
        </div>
      </div>
    </>
  );
};

export default BackgroundCsvReport;
